import React, { useMemo, useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import ParticipantList from "./ParticipantList";
import { calculateDuration, calculateDurationMinutes } from "./time-utils";
import { FaCheck, FaEye, FaCalendarAlt, FaPen } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getAssessmentByUserAndEvent } from '../../firebase';
import { getCourseConfig } from '../../services/CourseConfigService';
import { computeRatingWindowEndMs, RATING_WINDOW_DAY_MS } from '../../utils/ratingWindow';

const ArtifactLink = React.memo(({ artifact, onClick }) => {
    const [isHovering, setIsHovering] = useState(false);

    let label = '';
    let tooltip = '';
    let href = '';
    let isButton = false;

    switch (artifact.type) {
        case 'file':
            label = artifact.item.name;
            tooltip = `${artifact.item.name}`;
            href = artifact.item.url;
            isButton = true;
            break;

        case 'pr':
            label = artifact.title;
            tooltip = `${artifact.filesChanged} file${artifact.filesChanged !== 1 ? 's' : ''} · +${artifact.codeChanges.additions} / -${artifact.codeChanges.deletions} · ${artifact.commits} commit${artifact.commits !== 1 ? 's' : ''}`;
            isButton = true;
            break;

        case 'googleDoc':
            const hasMeta = !artifact.missingMeta;
            const added = artifact.wordChange?.added ?? 0;
            const removed = artifact.wordChange?.removed ?? 0;

            label = artifact.title || 'Google Doc';

            if (!hasMeta) {
                tooltip = 'Metadata not fetched';
                href = artifact.link; // disable link entirely
                isButton = true;
            } else {
                const versionText = !artifact.wordChange
                    ? 'Version 1'
                    : added === 0 && removed === 0
                        ? 'Same Doc'
                        : `+${added} / -${removed}`;

                tooltip = `${versionText} · ${artifact.wordCount ?? 0} word${artifact.wordCount === 1 ? '' : 's'}`;
                href = artifact.link;
                isButton = true;
            }
            break;

        case 'figma':
            const hasFigmaMeta = !artifact.missingMeta;
            label = artifact.title || artifact.meta?.fileName || 'Figma File';

            if (!hasFigmaMeta) {
                tooltip = 'Metadata not fetched';
                href = artifact.link;
                isButton = true;
                break;
            }

            const meta = artifact.meta || {};
            const count = meta.versionChange?.count ?? 0;
            const status = meta.versionChange?.status || '—';
            const type = meta.fileType
                ? meta.fileType.charAt(0).toUpperCase() + meta.fileType.slice(1)
                : 'Unknown';

            tooltip = `${count} edit${count !== 1 ? 's' : ''} · ${status === 'initial'
                ? 'Version 1'
                : status === 'no_change'
                    ? 'Same'
                    : status === 'updated'
                        ? 'New version'
                        : status
                } · ${type}`;

            href = artifact.link;
            isButton = true;
            break;

        case 'discord':
            label = `${artifact.messageCount ?? 0} message${(artifact.messageCount ?? 0) !== 1 ? 's' : ''}`;
            tooltip = 'Discord chat';
            isButton = true;
            break;

        default:
            label = artifact.label || `(Unknown ${artifact.type})`;
            href = artifact.url || '#';
            break;
    }

    const Element = isButton ? 'button' : 'a';

    const handleClick = (e) => {
        if (isButton && onClick) {
            e.preventDefault();
            e.stopPropagation();
            onClick(artifact, e);
        }
    };

    return (
        <HoverWrapper onMouseEnter={() => setIsHovering(true)} onMouseLeave={() => setIsHovering(false)}>
            <UnifiedLink
                as={Element}
                href={!isButton ? href : undefined}
                target={!isButton ? "_blank" : undefined}
                rel={!isButton ? "noopener noreferrer" : undefined}
                onClick={isButton || artifact.type === 'file' ? handleClick : undefined}
                $isRed={artifact.type === 'googleDoc' && artifact.missingMeta}
            >
                <TruncatedText>{label}</TruncatedText>
            </UnifiedLink>
            {isHovering && tooltip && (
                <HoverInfo>{tooltip}</HoverInfo>
            )}
        </HoverWrapper>
    );
});

const TagBadge = ({ type }) => {
    const TAG_STYLES = {
        pr: {
            label: 'PR',
            bg: '#bfdbfe',
            text: '#1d4ed8',
        },
        file: {
            label: 'File',
            bg: '#fef3c7',
            text: '#92400e',
        },
        googleDoc: {
            label: 'GDoc',
            bg: '#d1fae5',
            text: '#065f46',
        },
        figma: {
            label: 'Figma',
            bg: '#ede9fe', // purple-100
            text: '#5b21b6', // purple-800
        },
        discord: {
            label: 'Discord',
            bg: '#e0e7ff', // indigo-100
            text: '#4338ca', // indigo-700
        },
    };
    const style = TAG_STYLES[type] || {
        label: type || 'Unknown',
        bg: '#f3f4f6',
        text: '#374151',
    };

    return (
        <Tag style={{ backgroundColor: style.bg, color: style.text }}>
            {style.label}
        </Tag>
    );
};

const ReviewProgress = ({ session, groupMembers, modificationCount = 0 }) => {
    const totalMembers = groupMembers?.length || 0;
    const completedReviews = session?.totalSubmitted || 0;
    const isFullyCompleted = totalMembers > 0 && completedReviews >= totalMembers;

    if (totalMembers === 0) {
        return <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>—</span>;
    }

    return (
        <ReviewProgressWrapper>
            <ProgressText $isCompleted={isFullyCompleted}>
                {isFullyCompleted && <FaCheck style={{ marginRight: '4px', fontSize: '12px' }} />}
                {completedReviews}/{totalMembers}
            </ProgressText>
            <ProgressStatus $isCompleted={isFullyCompleted}>
                {isFullyCompleted ? 'completed' : 'pending'}
            </ProgressStatus>
            {modificationCount > 0 && (
                <ModificationBadge>
                    {modificationCount} change{modificationCount !== 1 ? 's' : ''}
                </ModificationBadge>
            )}
        </ReviewProgressWrapper>
    );
};

const DeadlineProgress = ({ session, effectiveDeadline = null, totalWindowMs = null, hasRated = false }) => {
    if (!session?.reviewDeadline && !effectiveDeadline) {
        return null;
    }

    // For re-rate, the caller passes the tighter window (artifact-modified + editWindow);
    // otherwise fall back to the session's review deadline.
    const deadline = effectiveDeadline ? new Date(effectiveDeadline) : new Date(session.reviewDeadline);
    const now = new Date();
    const remainingMs = deadline - now;
    const isOverdue = remainingMs <= 0;

    // The window has closed but this user already rated, so nothing is overdue for them.
    if (isOverdue && hasRated) {
        return (
            <DeadlineWrapper>
                <DeadlineText style={{ color: '#6b7280' }}>Closed</DeadlineText>
            </DeadlineWrapper>
        );
    }

    const daysRemaining = Math.round(remainingMs / (1000 * 60 * 60 * 24));
    const totalMs = totalWindowMs
        ?? (session.reviewDeadlineDays
            ? session.reviewDeadlineDays * 24 * 60 * 60 * 1000
            : (deadline - new Date(session.date)));
    const percentage = totalMs > 0
        ? Math.max(0, Math.min(100, (remainingMs / totalMs) * 100))
        : 0;

    let barColor, textColor;
    if (isOverdue) {
        barColor = '#ef4444';
        textColor = '#dc2626';
    } else if (percentage <= 25) {
        barColor = '#f59e0b'; // amber - urgent
        textColor = '#d97706';
    } else if (percentage <= 50) {
        barColor = '#3b82f6'; // blue - coming up
        textColor = '#2563eb';
    } else {
        barColor = '#10b981'; // green - plenty of time
        textColor = '#059669';
    }

    const formatHoursMinutes = (ms) => {
        const totalMinutes = Math.floor(ms / 60000);
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        if (hours > 0 && minutes > 0) return `${hours}h ${minutes}m remaining`;
        if (hours > 0) return `${hours}h remaining`;
        if (minutes > 0) return `${minutes}m remaining`;
        return 'Due now';
    };

    const formatOverdue = (ms) => {
        const totalMinutes = Math.floor(ms / 60000);
        const days = Math.floor(totalMinutes / (24 * 60));
        const hours = Math.floor(totalMinutes / 60);
        if (days > 0) return `Overdue by ${days}d`;
        if (hours > 0) return `Overdue by ${hours}h`;
        return `Overdue by ${Math.max(1, totalMinutes)}m`;
    };

    return (
        <DeadlineWrapper>
            <DeadlineText style={{ color: textColor }}>
                {isOverdue ? (
                    <>{formatOverdue(-remainingMs)}</>
                ) : remainingMs < 24 * 60 * 60 * 1000 ? (
                    <>{formatHoursMinutes(remainingMs)}</>
                ) : (
                    <>{daysRemaining}d remaining</>
                )}
            </DeadlineText>
            {!isOverdue && (
                <ProgressBarContainer>
                    <ProgressBarFill $percentage={percentage} $color={barColor} />
                </ProgressBarContainer>
            )}
        </DeadlineWrapper>
    );
};

const AdminActionButtons = ({ session, onSetDeadline, onViewResults }) => {
    const handleViewResults = (e) => {
        e.stopPropagation();
        if (onViewResults) {
            onViewResults(session);
        }
    };

    const handleSetDeadline = (e) => {
        e.stopPropagation();
        if (onSetDeadline) {
            onSetDeadline(session);
        }
    };

    return (
        <ActionsWrapper>
            <ActionButton $variant="success" onClick={handleViewResults}>
                {/* <FaEye style={{ marginRight: '6px' }} /> */}
                View Results
            </ActionButton>
            {/* <ActionButton $variant="primary" onClick={handleSetDeadline}>
                Set Deadline
            </ActionButton> */}
            <DeadlineProgress session={session} />
        </ActionsWrapper>
    );
};

const ActionButtons = ({ session, currentUserId }) => {
    const navigate = useNavigate();
    const [userHasRated, setUserHasRated] = useState(false);
    const [isRatingStale, setIsRatingStale] = useState(false);
    const [isCheckingAssessment, setIsCheckingAssessment] = useState(true);
    const [editWindowDays, setEditWindowDays] = useState(null);
    const [editEnabled, setEditEnabled] = useState(true);

    // Check if current user has already submitted an assessment for this session
    useEffect(() => {
        const checkUserAssessment = async () => {
            if (!session?.eventId || !currentUserId) {
                setIsCheckingAssessment(false);
                return;
            }
            try {
                const [existing, courseConfig] = await Promise.all([
                    getAssessmentByUserAndEvent(session.eventId, currentUserId),
                    session?.courseId
                        ? getCourseConfig(session.courseId).catch(() => null)
                        : Promise.resolve(null),
                ]);
                setEditWindowDays(courseConfig?.settings?.editWindowDays ?? 7);
                setEditEnabled(courseConfig?.settings?.editEnabled ?? true);
                if (existing) {
                    const artifactLastModified = session.artifactLastModifiedAt;
                    if (artifactLastModified) {
                        const modifiedMs = artifactLastModified.toMillis();
                        const ratedMs = Math.max(
                            existing.submittedAt?.toMillis() || 0,
                            existing.updatedAt?.toMillis() || 0
                        );
                        if (ratedMs < modifiedMs) {
                            setIsRatingStale(true);
                            setUserHasRated(false);
                            return;
                        }
                    }
                    setUserHasRated(true);
                }
            } catch (error) {
                console.error('Error checking user assessment:', error);
            } finally {
                setIsCheckingAssessment(false);
            }
        };
        checkUserAssessment();
    }, [session?.eventId, currentUserId, session?.artifactLastModifiedAt, session?.courseId]);

    const handleSubmitRating = (e) => {
        e.stopPropagation();
        navigate('/work-assessment', { state: { session } });
    };

    const handleEditRating = (e) => {
        e.stopPropagation();
        navigate('/work-assessment', { state: { session } });
    };

    if (isCheckingAssessment) {
        return (
            <ActionsWrapper>
                <StatusText style={{ color: '#9ca3af' }}>Loading...</StatusText>
            </ActionsWrapper>
        );
    }

    const isDeadlineExpired = session?.reviewDeadline && new Date() > new Date(session.reviewDeadline);

    if (isDeadlineExpired) {
        if (userHasRated) {
            const handleViewRating = (e) => {
                e.stopPropagation();
                navigate('/work-assessment', { state: { session, viewMode: true } });
            };
            return (
                <ActionsWrapper>
                    <ActionButton $variant="view" onClick={handleViewRating}>
                        View My Rating
                    </ActionButton>
                    <DeadlineProgress session={session} hasRated />
                </ActionsWrapper>
            );
        }
        return (
            <ActionsWrapper>
                <ActionButton $variant="locked" disabled>
                    Rating Closed
                </ActionButton>
                <DeadlineProgress session={session} />
            </ActionsWrapper>
        );
    }

    // User has already submitted their assessment
    if (userHasRated) {
        if (!editEnabled) {
            const handleViewRating = (e) => {
                e.stopPropagation();
                navigate('/work-assessment', { state: { session, viewMode: true } });
            };
            return (
                <ActionsWrapper>
                    <ActionButton $variant="view" onClick={handleViewRating}>
                        View My Rating
                    </ActionButton>
                    <DeadlineProgress session={session} />
                </ActionsWrapper>
            );
        }
        return (
            <ActionsWrapper>
                <ActionButton $variant="success" onClick={handleEditRating}>
                    {/* <FaPen style={{ marginRight: '6px' }} /> */}
                    Edit My Rating
                </ActionButton>
                <DeadlineProgress session={session} />
            </ActionsWrapper>
        );
    }

    // Artifact changed after user rated, students needs re-rating
    if (isRatingStale) {
        const handleReRate = (e) => {
            e.stopPropagation();
            navigate('/work-assessment', { state: { session, isReRate: true } });
        };
        // Effective deadline for re-rate: min(artifactLastModifiedAt + editWindowDays, reviewDeadline)
        let effectiveDeadline = null;
        let totalWindowMs = null;
        if (editWindowDays != null) {
            const endMs = computeRatingWindowEndMs({
                isReRate: true,
                artifactLastModifiedAt: session?.artifactLastModifiedAt,
                submittedAt: null,
                reviewDeadline: session?.reviewDeadline,
                editWindowDays,
            });
            if (endMs != null) {
                effectiveDeadline = new Date(endMs);
                totalWindowMs = editWindowDays * RATING_WINDOW_DAY_MS;
            }
        }
        return (
            <ActionsWrapper>
                <ActionButton $variant="pending" onClick={handleReRate}>
                    Re-Rate
                </ActionButton>
                <DeadlineProgress
                    session={session}
                    effectiveDeadline={effectiveDeadline}
                    totalWindowMs={totalWindowMs}
                />
            </ActionsWrapper>
        );
    }

    // Default: user has not submitted yet
    return (
        <ActionsWrapper>
            <ActionButton $variant="primary" onClick={handleSubmitRating}>
                Submit Rating
            </ActionButton>
            <DeadlineProgress session={session} />
        </ActionsWrapper>
    );
};

const ArtifactItem = React.memo((props) => {
    const { session, setActivePR, setActiveDoc, setActiveFigma, setActiveDiscord, setActiveFile, filterTag, isAdminView = false, groupMembers, onSetDeadline, onViewResults, modificationCount = 0 } = props;
    const { user, courseInfo } = useAuth();
    const deadlineEnabled = courseInfo?.deadlineEnabled !== false;

    const safeSession = session || {
        startTime: "",
        endTime: "",
        isCrossDate: false,
        files: [],
        googleDocs: [],
        prMeta: null,
        prLink: null,
        figmaFiles: [],
        discord: null,
        title: "",
        date: "",
    };

    const duration = useMemo(() => calculateDuration(
        safeSession.startTime,
        safeSession.endTime,
        safeSession.isCrossDate
    ), [safeSession.startTime, safeSession.endTime, safeSession.isCrossDate]);

    const durationMinutes = useMemo(() => calculateDurationMinutes(
        safeSession.startTime,
        safeSession.endTime,
        safeSession.isCrossDate
    ), [safeSession.startTime, safeSession.endTime, safeSession.isCrossDate]);

    const getRowBackgroundColor = () => {
        if (!isAdminView) return 'white';
        if (durationMinutes > 300) return '#FEF2F2'; // light red background for > 5 hours
        if (durationMinutes > 180) return '#FFF7ED'; // light orange background for > 3 hours
        return 'white';
    };

    const getDurationTextColor = () => {
        if (!isAdminView) return 'inherit';
        if (durationMinutes > 300) return '#dc2626'; // red text for > 5 hours
        if (durationMinutes > 180) return '#ea580c'; // orange text for > 3 hours
        return 'inherit';
    };

    const prItems = useMemo(() => {
        if (safeSession.prMeta && safeSession.prLink) {
            return [{
                type: 'pr',
                prLink: safeSession.prLink,
                title: safeSession.prMeta.title || 'Untitled PR',
                body: safeSession.prMeta.body,
                commits: safeSession.prMeta.commitCount,
                filesChanged: safeSession.prMeta.changedFiles,
                codeChanges: { additions: safeSession.prMeta.additions, deletions: safeSession.prMeta.deletions },
                collaborators: safeSession.prMeta.collaborators,
                coAuthors: safeSession.prMeta.coAuthors,
                state: safeSession.prMeta.state
            }];
        }
        return [];
    }, [safeSession.prMeta, safeSession.prLink]);

    const fileItems = useMemo(() => (
        (safeSession.files || []).map(file => ({
            type: 'file',
            item: file
        }))
    ), [safeSession.files]);

    const googleDocItems = useMemo(() => {
        if (!safeSession.googleDocs || !Array.isArray(safeSession.googleDocs) && safeSession.googleDocs.length === 0) return [];
        return safeSession.googleDocs.map((d) => {
            const meta = d?.meta;
            const hasMeta = meta && typeof meta === "object";

            return {
                type: 'googleDoc',
                title: hasMeta ? meta.title : d?.name || 'Google Doc Link',
                wordCount: hasMeta ? meta.wordCount : null,
                wordChange: hasMeta ? meta.wordChange : null,
                collaborators: hasMeta ? meta.collaborators : [],
                pdfStorage: hasMeta ? meta.pdfStorage: null,
                link: d?.link,
                previewUrl: hasMeta ? meta.previewUrl : null,
                missingMeta: !hasMeta,
            };
        });

    }, [safeSession.googleDocs]);

    const figmaItems = useMemo(() => {
        if (!safeSession.figmaFiles || !Array.isArray(safeSession.figmaFiles) || safeSession.figmaFiles.length === 0) return [];
        return safeSession.figmaFiles.map((f) => {
            const meta = f?.meta;
            const hasMeta = meta && typeof meta === "object";

            return {
                type: 'figma',
                title: hasMeta ? meta.fileName : f?.name || 'Figma File',
                link: f?.link || meta?.pdfStorage?.url,
                missingMeta: !hasMeta,
                meta: hasMeta ? meta : null,   
            };
        });
    }, [safeSession.figmaFiles]);

    const discordItems = useMemo(() => {
        const discord = safeSession.discord;
        if (!discord?.messages?.length) return [];
        return [{
            type: 'discord',
            messages: discord.messages,
            messageCount: discord.messages.length,
        }];
    }, [safeSession.discord]);

    const artifacts = useMemo(() => {
        return [
            ...prItems,
            ...fileItems,
            ...googleDocItems,
            ...figmaItems,
            ...discordItems,
        ];
    }, [prItems, fileItems, googleDocItems, figmaItems, discordItems]);


    const filteredArtifacts = useMemo(() => {
        return filterTag === 'all'
            ? artifacts
            : artifacts.filter(a => a.type === filterTag);
    }, [filterTag, artifacts]);

    const handleArtifactClick = useCallback((artifact) => {
        if (artifact.type === "pr") {
            setActivePR?.(artifact);
        } else if (artifact.type === "googleDoc") {
            setActiveDoc?.(artifact);
        } else if (artifact.type === "figma") {
            setActiveFigma?.(artifact);
        } else if (artifact.type === "discord") {
            setActiveDiscord?.(artifact);
        } else if (artifact.type === "file") {
            setActiveFile?.(artifact);
        }
    }, [setActivePR, setActiveDoc, setActiveFigma, setActiveDiscord, setActiveFile]);

    const handleRowClick = useCallback((artifact, e) => {
        // if (artifact.type === "file") {
            // For file types, open the URL in a new tab (same as clicking the link)
            // if (artifact.item?.url) {
            //     window.open(artifact.item.url, '_blank', 'noopener,noreferrer');
            // }
        // } else {
            // For other types (PR, GoogleDoc, Figma), open the popup
        //     handleArtifactClick(artifact);
        // }
        handleArtifactClick(artifact);
    }, [handleArtifactClick]);

    if (!session || typeof session !== "object") {
        console.warn("ArtifactItem skipped: invalid session", session);
        return null;
    }

    if (filteredArtifacts.length === 0) return null;


    return (
        <SessionWrapper>
            {filteredArtifacts.map((artifact, index) => (
                <StyledArtifactItem
                    key={index}
                    $bgColor={getRowBackgroundColor()}
                    $deadlineEnabled={deadlineEnabled}
                    onClick={(e) => handleRowClick(artifact, e)}
                >
                    <div className="name-column">
                        <ArtifactLink artifact={artifact} />
                    </div>
                    <div className="tag-column">
                        <TagBadge type={artifact.type} />
                    </div>

                    {index === 0 ? (
                        <>
                            <div className="participants-column">
                                <ParticipantList session={session} />
                            </div>
                            <div className="activity-column">
                                <TruncatedText title={session.title}>
                                    {session.title}
                                </TruncatedText>
                            </div>
                            <div className="date-column" style={{ whiteSpace: 'nowrap' }}>{session.date}</div>
                            <div className="start-time-column" style={{ whiteSpace: 'nowrap' }}>{session.startTime}</div>
                            <EndTimeColumn $isRed={session.isCrossDate} style={{ whiteSpace: 'nowrap' }}>
                                {session.endTime}
                            </EndTimeColumn>
                            <DurationColumn $color={getDurationTextColor()}>
                                {duration}
                            </DurationColumn>
                            {deadlineEnabled && (
                                <div className="review-progress-column">
                                    <ReviewProgress session={session} groupMembers={groupMembers} modificationCount={isAdminView ? modificationCount : 0} />
                                </div>
                            )}
                            {deadlineEnabled && (
                                <div className="actions-column">
                                    {isAdminView ? (
                                        <AdminActionButtons session={session} onSetDeadline={onSetDeadline} onViewResults={onViewResults} />
                                    ) : (
                                        <ActionButtons session={session} currentUserId={user?.email} />
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="participants-column" />
                            <div className="activity-column" />
                            <div className="date-column" />
                            <div className="start-time-column" />
                            <EndTimeColumn />
                            <DurationColumn />
                            {deadlineEnabled && <div className="review-progress-column" />}
                            {deadlineEnabled && <div className="actions-column" />}
                        </>
                    )}
                </StyledArtifactItem>
            ))}
        </SessionWrapper>
    );
});

const EndTimeColumn = styled.div`
    color: ${(props) => (props.$isRed ? "#c51212" : "inherit")};
    display: flex;
    align-items: center;
    padding: 0 8px;
    min-width: 0;
`;

const DurationColumn = styled.div`
    color: ${(props) => props.$color || "inherit"};
    display: flex;
    align-items: center;
    padding: 0 8px;
    min-width: 0;
    font-weight: ${(props) => props.$color && props.$color !== "inherit" ? "700" : "inherit"};
`;

const StyledArtifactItem = styled.article`
    display: grid;
    padding: 1rem 1.5rem;
    grid-template-columns: ${({ $deadlineEnabled }) =>
        $deadlineEnabled
            ? 'minmax(0,2.8fr) minmax(45px,0.75fr) minmax(135px,1fr) minmax(180px,3fr) minmax(130px,1.2fr) minmax(130px,1.2fr) minmax(130px,1.2fr) minmax(0,0.85fr) minmax(100px,1.2fr) minmax(150px,1.8fr)'
            : 'minmax(0,2.8fr) minmax(45px,0.75fr) minmax(135px,1fr) minmax(180px,3fr) minmax(130px,1.2fr) minmax(130px,1.2fr) minmax(130px,1.2fr) minmax(0,0.85fr)'};
    background-color: ${(props) => props.$bgColor || 'white'};
    text-align: left;
    border-bottom: none;
    transition: background-color 0.2s ease;
    cursor: pointer;

    &:hover {
        background-color: ${(props) => {
            if (!props.$bgColor || props.$bgColor === 'white') return '#f3f4f6'; // gray hover for white
            if (props.$bgColor === '#FEF2F2') return '#FEE2E2'; // darker red hover
            if (props.$bgColor === '#FFF7ED') return '#FFEDD5'; // darker orange hover
            return props.$bgColor;
        }};
    }

    > div {
        display: flex;
        align-items: center;
        padding: 0 8px;
        min-width: 0; /* This is important for text truncation to work in grid items */
    }
`;

const UnifiedLink = styled.a`
  background: none;
  border: none;
  padding: 0;
  text-align: left;
  color: ${(p) => (p.$isRed ? '#dc2626' : '#9333ea')};
  cursor: pointer;
  text-decoration: none;
  width: 100%;
  font: inherit;
  display: block;

  &:hover {
    color: ${(p) => (p.$isRed ? '#b91c1c' : '#5d298e')};
  }
`;

const TruncatedText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  word-break: break-word;
  font-weight: 500;
  width: 100%;
`;

const SessionWrapper = styled.div`
    background-color: white;
    // Only draw a border after the full session
    & + & {
        border-top: 1px solid #d1d5db; /* Tailwind gray-300 for lighter touch */
    }
`;


const Tag = styled.span`
  padding: 4px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  border-radius: 6px;
  white-space: nowrap;
`;

const HoverWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const HoverInfo = styled.div`
  position: absolute;
  top: 120%;
  left: 0;
  background: white;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  padding: 6px 10px;
  font-size: 12px;
  color: #111827;
  white-space: pre-wrap;
  z-index: 10;
  max-width: 250px;
`;

const ReviewProgressWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  width: 100%;
`;

const ProgressText = styled.div`
  display: flex;
  align-items: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${props => props.$isCompleted ? '#10b981' : '#111827'};
`;

const ProgressStatus = styled.div`
  font-size: 0.75rem;
  color: ${props => props.$isCompleted ? '#10b981' : '#6b7280'};
`;

const ModificationBadge = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: #d97706;
  background-color: #fef3c7;
  border: 1px solid #fde68a;
  border-radius: 4px;
  padding: 1px 6px;
  margin-top: 2px;
  width: fit-content;
`;

const ActionsWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
`;

const ActionButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 12px;
    font-size: 0.875rem;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    border: none;

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
            background-color: #3b82f6;
            color: white;
            &:hover { background-color: #2563eb; }
        `;
      case 'success':
        return `
            background-color: #10b981;
            color: white;
            &:hover { background-color: #059669; }
        `;
      case 'pending':
        return `
            background-color: #f59e0b;
            color: white;
            &:hover { background-color: #d97706; }
        `;
      case 'outline':
        return `
            background-color: white;
            color: #374151;
            border: 1px solid #d1d5db;
            &:hover { background-color: #f9fafb; }
        `;
      case 'outline-success':
        return `
            background-color: white;
            color: #10b981;
            border: 1px solid #10b981;
            &:hover { background-color: #ecfdf5; }
        `;
      case 'view':
        return `
            background-color: #f3f4f6;
            color: #374151;
            &:hover { background-color: #e5e7eb; }
        `;
      case 'locked':
        return `
            background-color: #e5e7eb;
            color: #9ca3af;
            cursor: not-allowed;
        `;
      default:
        return `
            background-color: #f3f4f6;
            color: #374151;
        `;
    }
  }}
`;

const StatusText = styled.div`
    display: flex;
    align-items: center;
    font-size: 0.875rem;
    color: #374151;
`;

const DeadlineWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: 100%;
    margin-top: 8px;
`;

const DeadlineText = styled.div`
    font-size: 0.75rem;
    font-weight: 500;
`;

const ProgressBarContainer = styled.div`
    width: 100%;
    height: 6px;
    background-color: #e5e7eb;
    border-radius: 3px;
    overflow: hidden;
`;

const ProgressBarFill = styled.div`
    height: 100%;
    width: ${props => props.$percentage}%;
    background-color: ${props => props.$color};
    transition: width 0.3s ease;
    border-radius: 3px;
`;

export default ArtifactItem;
